// Shared between the staff module and the family view inside My Notice.
//
// Both sides read the same records and have to agree on what a status means and what a subject or
// level is called. Kept here rather than duplicated because the two screens are written for
// different audiences and will drift apart otherwise -- the platform has already paid for that
// once, with two modules keeping their own copy of the same authorised-staff list.

export const SCOPE_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const SCOPE_STATUS_LABEL = {
  [SCOPE_STATUS.DRAFT]: 'Draft',
  [SCOPE_STATUS.PENDING]: 'Awaiting approval',
  [SCOPE_STATUS.APPROVED]: 'Published',
  [SCOPE_STATUS.REJECTED]: 'Sent back',
};

export const SCOPE_STATUS_COLOUR = {
  [SCOPE_STATUS.DRAFT]: 'var(--color-text-muted)',
  [SCOPE_STATUS.PENDING]: 'var(--color-warning-text)',
  [SCOPE_STATUS.APPROVED]: 'var(--color-success-text)',
  [SCOPE_STATUS.REJECTED]: 'var(--color-danger-text)',
};

export const scopeId = () => `ES-${Date.now().toString().slice(-6)}-${Math.floor(Math.random() * 10000)}`;

/** Look-ups built once from metadata/system_settings, so no screen resolves a code by hand. */
export const buildScopeLookups = (settings) => {
  const d = settings?.dropdowns || {};
  // By code, ascending. The school's subject list has genuine duplicate names -- two Chemistry
  // rows, three Mathematics -- so a picker showing names alone cannot be used, and alphabetical by
  // name would interleave the junior and senior versions of the same subject.
  const subjects = (d.subjects || [])
    .filter(x => x.active !== false)
    .sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''), undefined, { numeric: true }));

  // Only sittings. Term 1, Term 2 and Annual are reporting periods -- they aggregate the marks from
  // the tests either side of them and nobody sits them, so there is nothing to revise for. Offering
  // them would produce scopes attached to something that never happens.
  const assessments = (d.assessments || [])
    .filter(x => x.active !== false && x.writtenTest !== false)
    .sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0));
  // Unfiltered, for DISPLAY only. The lists above decide what may be chosen; these decide what an
  // existing record is called. Resolving a saved scope against the filtered list would turn it back
  // into a bare code the day a school retires a subject or unticks Written Test -- the record would
  // still be correct and would suddenly read as "CHEM2" to a parent.
  const allSubjects = d.subjects || [];
  const allAssessments = d.assessments || [];
  const levels = (d.classLevels || []).filter(x => x.active !== false);
  const classes = (d.classes || []).filter(x => x.active !== false);
  const departments = (d.departments || []).filter(x => x.active !== false);

  const name = (list, code, fallback) => {
    const hit = list.find(x => x.code === code);
    return hit ? (hit.nameEn || hit.name || hit.code) : (code || fallback || '');
  };

  return {
    subjects, assessments, levels, classes, departments,
    subjectName: (code) => name(allSubjects, code),
    // Code first, because it is the only part guaranteed unique. "CHEM1 - Chemistry" and
    // "CHEM2 - Chemistry" are two subjects; "Chemistry" twice is a list nobody can pick from.
    subjectLabel: (code) => {
      const hit = allSubjects.find(x => x.code === code);
      if (!hit) return code || '';
      const n = hit.nameEn || hit.name || '';
      return n ? `${hit.code} - ${n}` : hit.code;
    },
    assessmentName: (code) => name(allAssessments, code),
    levelName: (code) => name(levels, code),
    departmentName: (code) => name(departments, code),
    // Which panel owns a subject. The link a school sets in System Settings -> Subjects; without it
    // there is no way to work out who signs the scope off, so the form says so rather than
    // presenting a subject that cannot be approved.
    departmentForSubject: (code) => (allSubjects.find(x => x.code === code)?.department || ''),
    // A class belongs to a level because the school said so. The leading-characters fallback keeps
    // things grouped for a school that has not filled it in -- 6A and 6B still land on 6 -- but the
    // configured value wins, the same rule the notice roster uses.
    levelForClass: (cls) => {
      const hit = classes.find(c => c.code === cls || c.nameEn === cls || c.name === cls);
      if (hit?.level) return hit.level;
      const lead = String(cls || '').match(/^[A-Za-z]*[0-9]+/);
      return lead ? lead[0] : '';
    },
  };
};

/** Newest year first, then assessment, then subject -- the order a school reads a scope list in. */
export const sortScopes = (a, b) => (
  String(b.schoolYear || '').localeCompare(String(a.schoolYear || ''))
  || String(a.assessmentCode || '').localeCompare(String(b.assessmentCode || ''))
  || String(a.subjectCode || '').localeCompare(String(b.subjectCode || ''))
  || String(a.classLevel || '').localeCompare(String(b.classLevel || ''), undefined, { numeric: true })
);
